import isBefore from 'date-fns/is_before';

/**
 * A custom interface for storing data (similar to localStorage)
 *
 * @interface BackingStore
 * @template T any JSON-serializable data type (uses a type guard at runtime)
 */
export interface BackingStore<T> {
  /**
   * Default value to return if not available in this store
   */
  defaultValue: T;

  /**
   * Set an item in this store
   *
   * @param {T} item JSON-serializable data type to store
   * @param {Date} expires date when this value should be refetched
   * @param {string} [scope] an additional key to scope this value
   */
  setItem(item: T, expires: Date, scope?: string): void;

  /**
   * Determine if the store includes this value (should return
   * false if the value is expired)
   *
   * @param {string} [scope] an additional key to scope this value
   * @returns {boolean} whether the item is valid and available in the store
   */
  hasItem(scope?: string): boolean;

  /**
   * Get the item (or default) from the store
   *
   * @param {string} [scope] an additiona key to scope this value
   * @returns {T} the stored value (deserialized from JSON)
   */
  getItem(scope?: string): T;
}

/**
 * A wrapping type for a JSON-serializable expiration and value
 */
export interface ExpiringValue<T> {
  /**
   * An ISO 8601 string representing when this value expires
   */
  expires: string;

  /**
   * The embedded JSON-serializable value (with the associated type)
   */
  value: T;
}

/**
 * A type for storing data in-memory instead of ExpiringValue<T> that is
 * specifically stored as a JSON string
 */
export interface DecodedExpiringValue<T> {
  expires: Date;
  value: T;
}

/**
 * A type guard for determining at runtime whether the value retrieved from
 * localStorage matches an ExpiringValue<T> type
 *
 * @param {*} expiringValue
 * @returns {expiringValue is ExpiringValue<any>}
 */
const isExpiringValue = (
  expiringValue: any
): expiringValue is ExpiringValue<any> => {
  return (
    typeof expiringValue.expires === 'string' &&
    typeof expiringValue.value === 'object'
  );
};

/**
 * Combine a key with an optional scope key for uniqueness in localStorage
 */
const withScopeKey = (key: string, scope?: string) => {
  return scope ? `${key}-${scope}` : key;
};

export const localStorageInMemoryCache: Record<
  string,
  DecodedExpiringValue<any>
> = {};

/**
 * Create a backing store that uses localStorage (or in-memory cache when
 * loaded) with custom expiration times
 *
 * @template T any object stored in localStorage
 * @param {string} key where all of the data is scoped under (uses scope
 * as suffix if passed)
 * @param {T} defaultValue the immediate value to return if not available
 * locally and/or still needs to be fetched
 * @param {(value: any) => value is T} isValue function with type guard
 * to verify value at runtime
 * @returns {BackingStore<T>} a place to store data that has a similar
 * interface to localStorage
 */
export const localStorageData = <T>(
  key: string,
  defaultValue: T,
  isValue: (value: any) => value is T
): BackingStore<T> => ({
  defaultValue,

  setItem(item, expires, scope) {
    const wrappedValue: ExpiringValue<T> = {
      expires: expires.toISOString(),
      value: item,
    };

    localStorage.setItem(
      withScopeKey(key, scope),
      JSON.stringify(wrappedValue)
    );
  },

  hasItem(scope) {
    const rawValue = localStorage.getItem(withScopeKey(key, scope));
    if (!rawValue) {
      return false;
    }

    const wrappedValue = JSON.parse(rawValue);
    if (!isExpiringValue(wrappedValue)) {
      return false;
    }

    const expires = new Date(wrappedValue.expires);
    return !isBefore(expires, new Date());
  },

  getItem(scope) {
    const fullKey = withScopeKey(key, scope);

    const inMemoryValue = localStorageInMemoryCache[fullKey];
    if (
      inMemoryValue &&
      isBefore(new Date(), inMemoryValue.expires) &&
      isValue(inMemoryValue.value)
    ) {
      return inMemoryValue.value;
    }

    const rawValue = localStorage.getItem(fullKey);
    if (!rawValue) {
      return defaultValue;
    }

    const wrappedValue = JSON.parse(rawValue);
    if (!isExpiringValue(wrappedValue)) {
      return defaultValue;
    }

    const expires = new Date(wrappedValue.expires);
    if (isBefore(expires, new Date())) {
      return defaultValue;
    }

    const value = wrappedValue.value;
    if (!isValue(value)) {
      return defaultValue;
    }

    localStorageInMemoryCache[fullKey] = { expires, value };

    return value;
  },
});
